import { debounce } from '@mui/material/utils';
import { BlockDomain, materializeTrackOrder } from '@strength/domain';
import { useRouter } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import { z } from 'zod';
import { useMutation } from '../../../../loco-sync';
import { trackEvent } from '../../../../reporting/reporting';
import { trpc } from '../../../../trpc/react';
import { useViewer } from '../../../context/viewer';
import { useFlags } from '../../../context/featureFlag';
import { useBootstrap } from '../../../hooks/useBootstrap';
import { useStorage } from '../../../hooks/useStorage';
import { toast } from '../../../hooks/useToast';
import { usePlanContext } from '../context/PlanContext';
import { buildBlockMoveInput } from './block-drag';
import { usePendingMoveLiveBlock } from './use-pending-move';
import {
  applyPendingReorder,
  usePendingReorder,
} from '../hooks/usePendingReorder';
import { sortTracks } from '../sort-tracks';
import { useTrackMutations } from '../hooks/useTrackMutations';
import { ConfirmBlockMoveModal } from './components/ConfirmBlockMoveModal';
import { AnnualPlannerUnavailableState } from './components/AnnualPlannerUnavailableState';
import { BlockPanel } from './components/BlockPanel';
import { MoveSubTrackModal } from './components/MoveSubTrackModal';
import { TrackPanel } from './components/TrackPanel';
import { ViewArchivedTrackDrawer } from './components/ViewArchivedTrackDrawer';
import { SIDE_PANEL_WIDTH } from './utils';
import { BlockForm } from './forms/block-form-schema';
import { BlockSheet } from './forms/BlockForm';
import { PhaseForm, PhaseSheet } from './forms/PhaseForm';
import { TrackForm } from './forms/TrackForm';
import {
  isEligibleMoveDestination,
  moveSubTrackErrorMessage,
} from './sub-track-move';
import { buildTimelineRows, expandEmptyMoveDestination } from './track-rows';
import { timelineData } from './timeline-data';
import {
  AddBlockData,
  Block,
  DateScale,
  TrackForEdit,
  TrackPreview,
} from './types';
import {
  createBlockInput,
  editBlockInput,
  isTrackDeleteDisabled,
} from './utils';

const TRACK_COLLAPSED_KEY = 'annual-planner-collapsed-tracks-by-team';
const TODAY_LEFT_OFFSET = 280;
const DEFAULT_DATE_SCALE = 'quarter';

export function TimelinePage() {
  const {
    teamId,
    selectedAthlete,
    filteredTracks,
    refetchTracks,
    phases,
    refetchPhases,
    teamDetail,
    blocks,
    focuses,
    minDate,
    timelineDate,
    setTimelineDate,
    // `archivedTracks` is unfiltered and stays the structural source of truth
    // (see `isParentArchived` and the drawer lookup below); the Archive section
    // renders `filteredArchivedTracks`.
    archivedTracks,
    filteredArchivedTracks,
    archivedSectionExpanded,
    collapsedGroupLabelIds,
    hasAdditionalTeams,
    athletes,
  } = usePlanContext();
  const {
    profile: { role },
    hasPermissions,
  } = useViewer();

  const { navigate } = useRouter();
  const { t } = useTranslation();
  const { subTrackReorganizationFlag } = useFlags();

  // Navigate to calendar view when clicking on a month header
  const handleMonthClick = useCallback(
    (date: DateTime) => {
      navigate({
        to: '/$teamId/plan/calendar',
        params: { teamId },
        search: { date: date.toFormat('yyyy-MM') },
      });
    },
    [navigate, teamId],
  );

  const [navWidth, setNavWidth] = useState<number>(0);
  const scrollPanelRef = useRef<HTMLDivElement>(null);
  const [dateScale, setDateScale] = useState<DateScale>(DEFAULT_DATE_SCALE);

  const timelineSyncGroups = useMemo(
    () => [
      {
        type: 'TeamCoach_Timeline' as const,
        teamId,
      },
    ],
    [teamId],
  );

  const { isLoading: isTimelineLoading } = useBootstrap(timelineSyncGroups);

  // This effect tracks the width of the nav bar, so we can accurately adjust the viewport of the
  // planner to allows horizontal scrolling
  useEffect(() => {
    const resizeObserver = new ResizeObserver((e) => {
      e.forEach((e) => {
        setNavWidth(e.contentRect.width || 0);
      });
    });
    // this id is set by the teamworksdev library
    const nav = document.getElementById('tw-shared-nav');
    if (nav) {
      resizeObserver.observe(nav);
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const timeline = useMemo(
    () => timelineData(minDate, dateScale),
    [minDate, dateScale],
  );

  // Scroll to a specific date, optionally using a different scale for offset calculation
  const scrollToDate = useCallback(
    (date: DateTime, scale?: DateScale) => {
      if (!scrollPanelRef?.current) return;
      // If a new scale is provided, compute timeline from scratch; otherwise use current
      const { dateHeaderScale: headerScale, timelineStart: start } = scale
        ? timelineData(minDate, scale)
        : timeline;
      const daysDiff = date.diff(start, 'days').days;
      const targetOffset = daysDiff * headerScale;
      scrollPanelRef.current.scrollLeft = targetOffset - TODAY_LEFT_OFFSET;
    },
    [minDate, timeline],
  );

  // Calculate the current date from scroll position (reverse of scrollToDate)
  const getDateFromScroll = useCallback(() => {
    if (!scrollPanelRef?.current) return null;
    const { dateHeaderScale: headerScale, timelineStart: start } = timeline;
    const scrollLeft = scrollPanelRef.current.scrollLeft;
    const daysDiff = (scrollLeft + TODAY_LEFT_OFFSET) / headerScale;
    return start.plus({ days: daysDiff });
  }, [timeline]);

  // Update timelineDate when user scrolls (debounced)
  const debouncedSetTimelineDate = useMemo(
    () =>
      debounce(() => {
        const date = getDateFromScroll();
        if (date) {
          setTimelineDate(date);
        }
      }, 150),
    [getDateFromScroll, setTimelineDate],
  );

  // Attach scroll listener to update timelineDate as user scrolls
  useEffect(() => {
    const scrollPanel = scrollPanelRef.current;
    if (!scrollPanel) return;

    scrollPanel.addEventListener('scroll', debouncedSetTimelineDate);
    return () => {
      scrollPanel.removeEventListener('scroll', debouncedSetTimelineDate);
      debouncedSetTimelineDate.clear();
    };
  }, [debouncedSetTimelineDate]);

  // Scroll to timelineDate on mount only (not on every timelineDate change)
  useEffect(() => {
    if (isTimelineLoading || !scrollPanelRef?.current) return;
    scrollToDate(timelineDate);
  }, [isTimelineLoading, scrollToDate, timelineDate]);

  // Scroll to today when "Today" button is clicked
  const resetScroll = useCallback(() => {
    if (isTimelineLoading || !scrollPanelRef?.current) return;
    const today = DateTime.now();
    scrollToDate(today);
    setTimelineDate(today);
  }, [isTimelineLoading, scrollToDate, setTimelineDate]);

  const prevDateScaleRef = useRef(dateScale);

  // Reset scroll to today when dateScale changes
  useEffect(() => {
    if (prevDateScaleRef.current !== dateScale) {
      prevDateScaleRef.current = dateScale;
      resetScroll();
    }
  }, [dateScale, resetScroll]);

  const [parentTrack, setParentTrack] = useState<TrackPreview | null>(null);
  const [addTrackOpen, setAddTrackOpen] = useState(false);
  const [blockIdToEdit, setBlockIdToEdit] = useState<string | null>(null);
  const [trackForEdit, setTrackForEdit] = useState<TrackForEdit | null>(null);
  const [viewArchivedTrackId, setViewArchivedTrackId] = useState<string | null>(
    null,
  );
  // Armed from a drag (destination known) or the context menu (destination
  // picked in the modal).
  const [moveSubTrackRequest, setMoveSubTrackRequest] = useState<{
    subTrack: TrackPreview;
    destinationTrack?: TrackPreview;
  } | null>(null);
  const { moveSubTrack } = useTrackMutations();
  const [phaseIdToEdit, setPhaseIdToEdit] = useState<string | null>(null);
  const [blockToCopy, setBlockToCopy] = useState<Block | undefined>(undefined);

  // Tracks that are collapsed per team, should not show sub-tracks
  const [collapsedByTeam, setCollapsedByTeam] = useStorage<
    Record<string, string[]>
  >(TRACK_COLLAPSED_KEY, {});
  const collapsedTracks = useMemo(
    () => (teamId ? (collapsedByTeam[teamId] ?? []) : []),
    [teamId, collapsedByTeam],
  );

  // An empty destination renders no collapse toggle, so a stale collapsed
  // entry would hide the arriving sub-track.
  const expandMoveDestinationIfEmpty = useCallback(
    (destinationTrackId: string) => {
      if (!teamId) return;
      setCollapsedByTeam((prev) => {
        const current = prev[teamId] ?? [];
        const next = expandEmptyMoveDestination(
          current,
          filteredTracks ?? [],
          destinationTrackId,
        );
        return next == null ? prev : { ...prev, [teamId]: next };
      });
    },
    [filteredTracks, teamId, setCollapsedByTeam],
  );

  // The drag entry knows its destination and renders optimistically; the
  // context-menu entry doesn't until the modal.
  const [pendingSubTrackMove, setPendingSubTrackMove] = useState<{
    subTrackId: string;
    destinationTrackId: string;
  } | null>(null);

  // The pending move materialized against the raw base — this is also the
  // reorder overlay's convergence base: a same-group reorder of a freshly
  // moved sub-track must be checked against a list that holds it under its
  // new parent, or the overlay clears early and the sub-track falls back to
  // the move's appended-last position.
  const moveAppliedTracks = useMemo(() => {
    const baseTracks = filteredTracks ?? [];
    if (!pendingSubTrackMove) return baseTracks;

    const destinationSiblings = baseTracks.filter(
      (track) =>
        track.parentTrackId === pendingSubTrackMove.destinationTrackId &&
        track.id !== pendingSubTrackMove.subTrackId,
    );
    const materializedOrder = new Map(
      materializeTrackOrder(destinationSiblings).map((t) => [
        t.id,
        t.sortOrder,
      ]),
    );

    const withOverrides = baseTracks.map((track) => {
      if (track.id === pendingSubTrackMove.subTrackId) {
        return {
          ...track,
          parentTrackId: pendingSubTrackMove.destinationTrackId,
          sortOrder: destinationSiblings.length + 1,
        };
      }
      const materializedSortOrder = materializedOrder.get(track.id);
      return materializedSortOrder == null
        ? track
        : { ...track, sortOrder: materializedSortOrder };
    });

    // sortOrders only take effect once the list is re-sorted by them.
    return sortTracks(withOverrides);
  }, [filteredTracks, pendingSubTrackMove]);

  const { pendingReorder, beginReorder, settleReorder } =
    usePendingReorder(moveAppliedTracks);

  // Reorder composes on top of the materialized move: against the raw base
  // the moved sub-track still has its old parent, so a same-group reorder of
  // it would land at the end of its new group.
  const tracksForRender = useMemo(
    () => applyPendingReorder(moveAppliedTracks, pendingReorder),
    [moveAppliedTracks, pendingReorder],
  );

  // Ordered row list shared by the sidebar and the timeline.
  const rows = useMemo(
    () =>
      buildTimelineRows({
        activeTracks: tracksForRender,
        archivedTracks: filteredArchivedTracks,
        allArchivedTracks: archivedTracks,
        collapsedTrackIds: collapsedTracks,
        collapsedGroupLabelIds,
        archivedSectionExpanded,
        hasAdditionalTeams,
        subTrackReorganizationFlag,
      }),
    [
      tracksForRender,
      filteredArchivedTracks,
      archivedTracks,
      collapsedTracks,
      collapsedGroupLabelIds,
      archivedSectionExpanded,
      hasAdditionalTeams,
      subTrackReorganizationFlag,
    ],
  );

  const viewArchivedTrack = useMemo(
    () =>
      archivedTracks.find((track) => track.id === viewArchivedTrackId) ?? null,
    [archivedTracks, viewArchivedTrackId],
  );

  const openAddTrackDrawer = useCallback(
    (open: boolean, parentTrack?: TrackPreview) => {
      setParentTrack(open && parentTrack ? parentTrack : null);
      setAddTrackOpen(open);
    },
    [],
  );

  // Refuse an ineligible drag destination up front: toast instead of opening a
  // modal and previewing a move the server would reject.
  const openMoveSubTrack = useCallback(
    (subTrack: TrackPreview, destinationTrack?: TrackPreview) => {
      if (
        destinationTrack &&
        !isEligibleMoveDestination(
          subTrack,
          subTrack.parentTrackId ?? '',
          destinationTrack,
          filteredTracks ?? [],
        )
      ) {
        toast({
          title: t('moveSubTrackNameCollision'),
          severity: 'error',
        });
        return;
      }

      setMoveSubTrackRequest({ subTrack, destinationTrack });
      if (destinationTrack) {
        expandMoveDestinationIfEmpty(destinationTrack.id);
        setPendingSubTrackMove({
          subTrackId: subTrack.id,
          destinationTrackId: destinationTrack.id,
        });
      }
    },
    [expandMoveDestinationIfEmpty, filteredTracks, t],
  );

  const closeMoveSubTrack = useCallback(() => {
    setMoveSubTrackRequest(null);
    setPendingSubTrackMove(null);
  }, []);

  // Read from the live list so synced renames/archives reach the modal; a
  // sub-track that leaves the list drops the modal.
  const moveSubTrackContext = useMemo(() => {
    if (!moveSubTrackRequest) return null;

    const tracks = filteredTracks ?? [];
    const subTrack = tracks.find(
      (t) => t.id === moveSubTrackRequest.subTrack.id,
    );
    const currentParentTrack = tracks.find(
      (t) => t.id === subTrack?.parentTrackId,
    );
    if (!subTrack || !currentParentTrack) return null;

    const destinationTrack = moveSubTrackRequest.destinationTrack
      ? tracks.find((t) => t.id === moveSubTrackRequest.destinationTrack?.id)
      : undefined;

    return { subTrack, currentParentTrack, destinationTrack };
  }, [moveSubTrackRequest, filteredTracks]);

  // A sync can invalidate the request without closing the modal; clear it and
  // the optimistic override so nothing dangles.
  useEffect(() => {
    if (moveSubTrackRequest && !moveSubTrackContext) {
      setMoveSubTrackRequest(null);
      setPendingSubTrackMove(null);
    }
  }, [moveSubTrackRequest, moveSubTrackContext]);

  // Close immediately and report the outcome by toast. Keep the optimistic
  // override up through the refetch on success so the sub-track doesn't flash
  // back to its old parent.
  const onConfirmMoveSubTrack = useCallback(
    (destinationTrack: TrackPreview) => {
      if (!moveSubTrackContext) return;
      const { subTrack } = moveSubTrackContext;
      setMoveSubTrackRequest(null);
      expandMoveDestinationIfEmpty(destinationTrack.id);
      setPendingSubTrackMove({
        subTrackId: subTrack.id,
        destinationTrackId: destinationTrack.id,
      });

      moveSubTrack({
        trackId: subTrack.id,
        destinationTrackId: destinationTrack.id,
        teamId,
      })
        .then(async () => {
          await refetchTracks();
          setPendingSubTrackMove(null);
          toast({
            description: t('subTrackMoved', {
              subTrack: subTrack.name,
              destination: destinationTrack.name,
            }),
            severity: 'success',
            title: t('success'),
          });
        })
        .catch((e: unknown) => {
          setPendingSubTrackMove(null);
          toast({
            title: moveSubTrackErrorMessage(e, {
              nameCollision: t('moveSubTrackNameCollision'),
              fallback: t('errorMovingSubTrack'),
            }),
            severity: 'error',
          });
        });
    },
    [
      expandMoveDestinationIfEmpty,
      moveSubTrackContext,
      moveSubTrack,
      refetchTracks,
      teamId,
      t,
    ],
  );

  // TODO: Big question if this should be run via loco-sync or not, if breaking down loco-sync boundary to training blocks
  const blockMutation = useMutation();

  const createBlock = (
    input: z.infer<typeof BlockDomain.Create.shape.input>,
  ) => {
    blockMutation.mutate(
      {
        module: 'Block',
        type: 'Create',
        input,
      },
      {
        onSuccess: () => {
          // log to gainsight
          trackEvent('Strength | Annual Planner | Create Block', {
            blockId: input.id,
            autoSchedule: input.autoSchedule,
          });

          if (input.blockType === 'bowling') {
            // redirect to the block page for the bowling block
            navigate({
              to: '/$teamId/block/$blockId',
              params: { teamId, blockId: input.id },
            });
          }
        },
      },
    );
  };

  const copyBlock = (input: z.infer<typeof BlockDomain.Copy.shape.input>) => {
    blockMutation.mutate(
      {
        module: 'Block',
        type: 'Copy',
        input,
      },
      {
        onSuccess: () => {
          // log to gainsight
          trackEvent('Strength | Annual Planner | Copy Block', {
            blockId: input.id,
            autoSchedule: input.autoSchedule,
          });

          if (input.blockType === 'bowling') {
            // redirect to the block page for the bowling block
            navigate({
              to: '/$teamId/block/$blockId',
              params: { teamId, blockId: input.id },
            });
          }
        },
      },
    );
  };

  const editBlock = (
    input: z.infer<typeof BlockDomain.Edit.shape.input>,
    callbacks?: { onSettled?: () => void },
  ) => {
    // loco-sync has no onSettled, so run the cleanup from both callbacks.
    const onSettled = () => callbacks?.onSettled?.();
    blockMutation.mutate(
      { module: 'Block', type: 'Edit', input },
      {
        // Roll back the optimistic dates and toast. A failed auto re-publish is
        // not this path: the dates are already committed.
        onError: () => {
          toast({
            title: t('errorEditingBlock'),
            severity: 'error',
          });
          onSettled();
        },
        onSuccess: onSettled,
      },
    );
  };

  const deleteBlock = (
    input: z.infer<typeof BlockDomain.Delete.shape.input>,
  ) => {
    blockMutation.mutate({
      module: 'Block',
      type: 'Delete',
      input,
    });
  };

  const cancelBlock = (
    input: z.infer<typeof BlockDomain.Cancel.shape.input>,
  ) => {
    blockMutation.mutate({
      module: 'Block',
      type: 'Cancel',
      input,
    });
  };

  // Zero when active, else tRPC; refetchTracks is a no-op under Zero.
  const { createTrack, editTrack, deleteTrack } = useTrackMutations({
    onSuccess: () => {
      refetchTracks();
    },
  });
  const reorderTracks = trpc.plan.reorderTracks.useMutation({
    onSuccess: () => {
      refetchTracks();
    },
    // Roll back the optimistic reorder on denial.
    onError: () => {
      refetchTracks();
    },
  });

  const onPhaseError = () => {
    toast({
      title: t('somethingWentWrong'),
      severity: 'error',
    });
  };
  const onPhaseChange = () => refetchPhases();
  const createPhase = trpc.phase.createPhase.useMutation({
    onSuccess: onPhaseChange,
    onError: onPhaseError,
  });

  const editPhase = trpc.phase.editPhase.useMutation({
    onSuccess: onPhaseChange,
    onError: onPhaseError,
  });

  const deletePhase = trpc.phase.deletePhase.useMutation({
    onSuccess: onPhaseChange,
  });

  const [addBlockData, setAddBlockData] = useState<AddBlockData | null>(null);
  const [newPhaseStartDate, setNewPhaseStartDate] =
    useState<DateTime<true> | null>(null);

  const onAddPhase = (form: PhaseForm) => {
    createPhase.mutate({
      id: v4(),
      ...form,
    });
  };

  const onEditPhase = (phaseId: string, form: PhaseForm) => {
    editPhase.mutate({
      id: phaseId,
      ...form,
    });
  };

  const onDeletePhase = (phaseId: string) => {
    deletePhase.mutate({ id: phaseId });
  };

  const onAddBlock = (form: BlockForm) => {
    if (!teamDetail || !addBlockData) {
      return;
    }
    createBlock(createBlockInput(teamDetail, form));
  };

  const onCopyBlock = (form: BlockForm) => {
    if (!teamDetail || !addBlockData || !blockToCopy?.id) {
      return;
    }
    copyBlock({
      ...createBlockInput(teamDetail, form),
      blockIdToCopy: blockToCopy.id,
    });
  };

  const onEditBlock = (blockId: string, form: BlockForm) => {
    if (!teamDetail) {
      return;
    }
    editBlock(editBlockInput(blockId, form));
  };

  // The drop proposes a start date; the Confirm modal decides. Nothing
  // persists until Confirm.
  const [pendingMove, setPendingMove] = useState<{
    block: Block;
    startDate: string;
  } | null>(null);
  const [isSavingMove, setIsSavingMove] = useState(false);

  const onDragMove = useCallback((block: Block, newStartDate: string) => {
    setPendingMove({ block, startDate: newStartDate });
  }, []);

  // Return focus to the dragged block (WAI-ARIA dialog pattern).
  const focusBlock = (blockId: string) =>
    (
      document.querySelector(
        `[data-block-id="${blockId}"]`,
      ) as HTMLElement | null
    )?.focus();

  const onCancelMove = useCallback(() => {
    const blockId = pendingMove?.block.id;
    setPendingMove(null);
    if (blockId) focusBlock(blockId);
  }, [pendingMove]);

  // A synced start-date change cancels the pending move: the drop was
  // relative to the old start.
  const liveMoveBlock = usePendingMoveLiveBlock(
    pendingMove,
    blocks ?? undefined,
    isSavingMove,
    onCancelMove,
  );

  const onConfirmMove = useCallback(
    (startDate: string) => {
      if (!pendingMove || !liveMoveBlock) return;
      const block = liveMoveBlock;
      setIsSavingMove(true);
      // Rides the shared Block.Edit shift path; on failure the optimistic
      // write rolls back and editBlock toasts.
      editBlock(buildBlockMoveInput(block, startDate), {
        onSettled: () => {
          setIsSavingMove(false);
          setPendingMove(null);
          focusBlock(block.id);
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pendingMove, liveMoveBlock],
  );

  const onDeleteBlock = (blockId: string) => {
    deleteBlock({ id: blockId });
    if (blockToCopy?.id === blockId) {
      setBlockToCopy(undefined);
    }
  };

  const onCancelBlock = (blockId: string) => {
    cancelBlock({ id: blockId });
  };

  const onAddTrack = (
    name: string,
    profileIds: string[],
    color: string | null,
  ) => {
    const trackId = v4();
    // Fire-and-forget; createTrack rejects on failure, so toast.
    void createTrack({
      id: trackId,
      name,
      teamId,
      color: color ?? null,
      parentTrackId: parentTrack?.id,
      athletes: profileIds.map((profileId) => ({
        profileId,
        id: v4(),
      })),
    }).catch(() => {
      toast({ title: t('errorCreatingTrack'), severity: 'error' });
    });
  };

  const onDeleteTrack = (trackId: string) => {
    const trackName = filteredTracks.find((t) => t.id === trackId)?.name;
    // Fire-and-forget; toast if deleteTrack rejects.
    void deleteTrack({ trackId, teamId })
      .then(() => {
        if (trackName) {
          toast({
            description: t('trackDeleted', { track: trackName }),
            severity: 'success',
            title: t('success'),
          });
        }
      })
      .catch(() => {
        toast({ title: t('errorDeletingTrack'), severity: 'error' });
      });
  };

  const onEditTrack = (
    trackId: string,
    name: string,
    athleteIds: string[],
    color: string | null,
  ) => {
    // Fire-and-forget; toast if editTrack rejects.
    void editTrack({
      id: trackId,
      name,
      athleteIds,
      color,
    }).catch(() => {
      toast({ title: t('errorEditingTrack'), severity: 'error' });
    });
  };

  // Reorder stays direct tRPC on both flag paths.
  const onReorderTracks = (reorderedTracks: TrackPreview[]) => {
    const seq = beginReorder(tracksForRender, reorderedTracks);
    // Settle via the per-call promise: a second mutate can replace the shared
    // observer before the first settles, and skipped callbacks would leave
    // the first drag's overlay unsettled — no rollback, no clear.
    reorderTracks
      .mutateAsync({ tracks: reorderedTracks })
      .then(() => settleReorder(seq, false))
      .catch(() => settleReorder(seq, true));
  };

  const trackPanelRef = useRef<HTMLDivElement>(null);
  const blockPanelRef = useRef<HTMLDivElement>(null);
  const handleTrackPanelScroll = (scroll: React.UIEvent<HTMLDivElement>) => {
    if (scroll.target instanceof HTMLDivElement) {
      if (blockPanelRef.current) {
        blockPanelRef.current.scrollTop = scroll.target.scrollTop;
      }
    }
  };
  const handleBlockPanelScroll = (scroll: React.UIEvent<HTMLDivElement>) => {
    if (scroll.target instanceof HTMLDivElement) {
      if (trackPanelRef.current) {
        trackPanelRef.current.scrollTop = scroll.target.scrollTop;
      }
    }
  };

  const toggleTrackCollapse = (trackId: string) => {
    if (!teamId) return;
    setCollapsedByTeam((prev) => {
      const current = prev[teamId] ?? [];
      const next = current.includes(trackId)
        ? current.filter((id) => id !== trackId)
        : [...current, trackId];
      return { ...prev, [teamId]: next };
    });
  };

  const isDeleteTrackDisabled = useMemo(
    () => isTrackDeleteDisabled(trackForEdit?.id, filteredTracks, role),
    [trackForEdit?.id, role, filteredTracks],
  );

  // Each view suppresses only its own section; losing both empties the pane.
  const canViewTracks = hasPermissions('plan.tracks', 'view');
  const canViewBlocks = hasPermissions('plan.blocks', 'view');

  if (!canViewTracks && !canViewBlocks) {
    return (
      <AnnualPlannerUnavailableState
        stateKey="unableToDisplayTracksOrBlocks"
        variant="pane"
      />
    );
  }

  return (
    <>
      <div className="w-full flex h-full overflow-hidden grow">
        {canViewTracks ? (
          <TrackPanel
            dateScale={dateScale}
            setDateScale={setDateScale}
            tracks={tracksForRender}
            rows={rows}
            openAddTrackDrawer={openAddTrackDrawer}
            setTrackForEdit={setTrackForEdit}
            onReorderTracks={onReorderTracks}
            resetScroll={resetScroll}
            trackPanelRef={trackPanelRef}
            handleScrollFirst={handleTrackPanelScroll}
            collapsedTracks={collapsedTracks}
            toggleTrackCollapse={toggleTrackCollapse}
            refetchTracks={refetchTracks}
            openViewArchivedTrackDrawer={setViewArchivedTrackId}
            openMoveSubTrack={openMoveSubTrack}
            subTrackReorganizationFlag={subTrackReorganizationFlag}
          />
        ) : (
          <div
            style={{ width: SIDE_PANEL_WIDTH }}
            className="h-full relative border-r flex-shrink-0"
          >
            <AnnualPlannerUnavailableState
              stateKey="unableToDisplayTracks"
              variant="inline"
            />
          </div>
        )}

        {!isTimelineLoading && (
          <BlockPanel
            navWidth={navWidth}
            timeline={timeline}
            scrollPanelRef={scrollPanelRef}
            blockPanelRef={blockPanelRef}
            teamId={teamId}
            phases={phases}
            tracks={tracksForRender}
            rows={rows}
            blocks={blocks ?? []}
            blockToCopy={blockToCopy}
            onCancelBlock={onCancelBlock}
            handleBlockPanelScroll={handleBlockPanelScroll}
            setNewPhaseStartDate={setNewPhaseStartDate}
            setPhaseIdToEdit={setPhaseIdToEdit}
            onDeletePhase={onDeletePhase}
            setBlockIdToEdit={setBlockIdToEdit}
            onDeleteBlock={onDeleteBlock}
            setBlockToCopy={setBlockToCopy}
            setAddBlockData={setAddBlockData}
            selectedAthleteId={selectedAthlete?.id}
            onMonthClick={handleMonthClick}
            onDragMove={onDragMove}
            pendingMove={
              pendingMove
                ? {
                    blockId: pendingMove.block.id,
                    startDate: pendingMove.startDate,
                    saving: isSavingMove,
                  }
                : null
            }
          />
        )}
      </div>

      {pendingMove && liveMoveBlock && (
        <ConfirmBlockMoveModal
          block={liveMoveBlock}
          proposedStartDate={pendingMove.startDate}
          isSaving={isSavingMove}
          onCancel={onCancelMove}
          onConfirm={onConfirmMove}
        />
      )}
      {(newPhaseStartDate || phaseIdToEdit) && (
        <PhaseSheet
          existingPhases={phases}
          onSave={(form) => {
            if (phaseIdToEdit) {
              onEditPhase(phaseIdToEdit, form);
            } else {
              onAddPhase(form);
            }
            setNewPhaseStartDate(null);
            setPhaseIdToEdit(null);
          }}
          open={!!(newPhaseStartDate || phaseIdToEdit)}
          defaultValues={
            phases.find((p) => p.id === phaseIdToEdit) || {
              startDate: newPhaseStartDate?.toISODate(),
            }
          }
          isEdit={!!phaseIdToEdit}
          onClose={() => {
            setNewPhaseStartDate(null);
            setPhaseIdToEdit(null);
          }}
          onDelete={() => {
            if (phaseIdToEdit) {
              onDeletePhase(phaseIdToEdit);
              setPhaseIdToEdit(null);
            }
          }}
        />
      )}

      {addBlockData?.startDate && (
        <BlockSheet
          onSave={(form) => {
            if (!addBlockData) {
              return;
            }

            addBlockData.isCopy ? onCopyBlock(form) : onAddBlock(form);
            setAddBlockData(null);
          }}
          focusOptions={focuses ?? []}
          open={!!addBlockData}
          cycleLabel={teamDetail?.settings.defaultCycleLabel ?? null}
          sessionLabel={teamDetail?.settings.defaultSessionLabel ?? null}
          defaultValues={{
            ...(addBlockData.isCopy
              ? blockToCopy
              : {
                  // Enable this by default
                  autoSchedule: true,
                }),
            // Put these after to override the copied block
            trackId: addBlockData.trackId,
            startDate: addBlockData.startDate.toISODate(),
          }}
          hasBeenPublished={false}
          onClose={() => setAddBlockData(null)}
          tracks={filteredTracks ?? []}
          state={addBlockData.isCopy ? 'copy' : 'add'}
        />
      )}
      {blockIdToEdit && (
        <BlockSheet
          onSave={(form) => {
            if (!blockIdToEdit) {
              return;
            }
            onEditBlock(blockIdToEdit, form);
            setBlockIdToEdit(null);
          }}
          focusOptions={focuses ?? []}
          open={!!blockIdToEdit}
          cycleLabel={teamDetail?.settings.defaultCycleLabel ?? null}
          sessionLabel={teamDetail?.settings.defaultSessionLabel ?? null}
          defaultValues={blocks?.find((b) => b.id === blockIdToEdit) ?? {}}
          hasBeenPublished={
            blocks?.find((b) => b.id === blockIdToEdit)?.hasPublishedWorkouts ??
            false
          }
          onClose={() => setBlockIdToEdit(null)}
          onDelete={() => {
            if (!blockIdToEdit) return;
            onDeleteBlock(blockIdToEdit);
            setBlockIdToEdit(null);
          }}
          tracks={filteredTracks ?? []}
          state="edit"
        />
      )}
      {addTrackOpen && (
        <TrackForm
          teamId={teamId}
          onSave={(name, athleteIds, color) => {
            onAddTrack(name, athleteIds, color);
            openAddTrackDrawer(false);
          }}
          onClose={() => openAddTrackDrawer(false)}
          open={addTrackOpen}
          defaultValues={undefined}
          track={{ parentTrack: parentTrack ?? undefined }}
        />
      )}
      {trackForEdit && (
        <TrackForm
          track={trackForEdit}
          teamId={teamId}
          onSave={(name, athleteIds, color) => {
            if (trackForEdit?.id) {
              onEditTrack(trackForEdit?.id, name, athleteIds, color);
            }
            setTrackForEdit(null);
          }}
          onClose={() => setTrackForEdit(null)}
          open={!!trackForEdit}
          onDelete={isDeleteTrackDisabled ? undefined : onDeleteTrack}
          defaultValues={filteredTracks?.find((t) => t.id === trackForEdit?.id)}
        />
      )}
      {moveSubTrackContext && (
        <MoveSubTrackModal
          open
          onClose={closeMoveSubTrack}
          subTrack={moveSubTrackContext.subTrack}
          currentParentTrack={moveSubTrackContext.currentParentTrack}
          destinationTrack={moveSubTrackContext.destinationTrack}
          allTracks={filteredTracks ?? []}
          onConfirm={onConfirmMoveSubTrack}
        />
      )}
      {viewArchivedTrack && (
        <ViewArchivedTrackDrawer
          track={viewArchivedTrack}
          athletes={athletes}
          // Can't unarchive a sub-track whose parent is archived.
          isParentArchived={
            viewArchivedTrack.parentTrackId != null &&
            archivedTracks.some(
              (track) => track.id === viewArchivedTrack.parentTrackId,
            )
          }
          onClose={() => setViewArchivedTrackId(null)}
          refetchTracks={refetchTracks}
        />
      )}
    </>
  );
}
